import React, { useRef, useEffect } from "react";
import FullCalendar from "@fullcalendar/react";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import {
  Box,
  Typography,
  Chip,
  useTheme,
  useMediaQuery,
  GlobalStyles,
} from "@mui/material";
import { ReservationScheduledResponse } from "../../types/generalTypes";
import { modalitiesConstant } from "../../constants/Global";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";

interface ModernCalendarViewProps {
  reservations: ReservationScheduledResponse[];
  displayedDate: Date;
  selectedCourts: string[];
  onTogglePaid: (reservation: ReservationScheduledResponse) => void;
  onEventDrop?: (info: any) => void;
  startHour?: number;
  endHour?: number;
  viewMode?: "daily" | "weekly";
}

const pastelColors = [
  { bg: "#FFE5E5", border: "#FFB3B3", text: "#a91313" },
  { bg: "#E5F3FF", border: "#B3D9FF", text: "#003D7A" },
  { bg: "#FFF3E5", border: "#FFD9B3", text: "#8B4500" },
  { bg: "#F0E5FF", border: "#D4B3FF", text: "#4B0082" },
  { bg: "#E5FFF3", border: "#B3FFD9", text: "#006B3D" },
  { bg: "#FFE5F8", border: "#FFB3E6", text: "#8B0045" },
  { bg: "#FFF9E5", border: "#FFECB3", text: "#8B7500" },
  { bg: "#E5F9FF", border: "#B3ECFF", text: "#005A8B" },
];

const ModernCalendarView: React.FC<ModernCalendarViewProps> = ({
  reservations,
  displayedDate,
  selectedCourts,
  onTogglePaid,
  onEventDrop,
  startHour = 7,
  endHour = 24,
  viewMode = "daily",
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const calendarRefs = useRef<(FullCalendar | null)[]>([]);

  useEffect(() => {
    calendarRefs.current.forEach((api) => {
      if (api) {
        api.getApi().gotoDate(displayedDate);
      }
    });
  }, [displayedDate]);

  const allEvents = reservations.map((reservation, index) => {
    const colorScheme = pastelColors[index % pastelColors.length];
    return {
      id: String(reservation.id),
      title: reservation.client,
      start: reservation.createdDate,
      end: reservation.endDate,
      backgroundColor: reservation.isPaid ? "#D4EDDA" : colorScheme.bg,
      borderColor: reservation.isPaid ? "#28A745" : colorScheme.border,
      textColor: reservation.isPaid ? "#155724" : colorScheme.text,
      extendedProps: {
        reservation,
        courtName: reservation.courtName,
        price: reservation.price,
        modality: reservation.modality,
        isPaid: reservation.isPaid,
      },
    };
  });

  const renderEventContent = (eventInfo: any) => {
    const { reservation, price, modality, isPaid, courtName } =
      eventInfo.event.extendedProps;
    const start = new Date(eventInfo.event.start);
    const end = new Date(eventInfo.event.end);
    const durationHours = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
    const hasSpace = durationHours >= 1;

    return (
      <Box
        sx={{
          padding: hasSpace ? "6px" : "2px 4px",
          height: "100%",
          borderRadius: "4px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          borderLeft: `3px solid ${eventInfo.borderColor}`,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "start",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                fontSize: hasSpace ? "0.75rem" : "0.7rem",
                lineHeight: 1.1,
              }}
            >
              {eventInfo.event.title}
            </Typography>
            {hasSpace && (
              <Chip
                label={`R$ ${price}`}
                size="small"
                sx={{
                  height: "16px",
                  fontSize: "0.7rem",
                  mt: "-2px",
                  backgroundColor: "rgba(255,255,255,0.5)",
                }}
              />
            )}
          </Box>
          <Box
            onClick={(e) => {
              e.stopPropagation();
              onTogglePaid(reservation);
            }}
            sx={{ cursor: "pointer", display: "flex", alignItems: "center" }}
          >
            {isPaid ? (
              <CheckCircleIcon sx={{ fontSize: 14, color: "#28A745" }} />
            ) : (
              <RadioButtonUncheckedIcon sx={{ fontSize: 14, opacity: 0.6 }} />
            )}
          </Box>
        </Box>
        {viewMode === "weekly" && hasSpace && (
          <Typography
            variant="caption"
            sx={{ fontSize: "0.65rem", fontWeight: 600, opacity: 0.9 }}
          >
            📍 {courtName}
          </Typography>
        )}
        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
          <Typography
            variant="caption"
            sx={{ fontSize: "0.65rem", fontWeight: 600 }}
          >
            {eventInfo.timeText}
          </Typography>
          <Typography
            variant="caption"
            sx={{ fontSize: "0.65rem", opacity: 0.8 }}
          >
            {modalitiesConstant[modality || 0]?.slice(0, 10)}
          </Typography>
        </Box>
      </Box>
    );
  };

  const commonGlobalStyles = {
    ".fc-now-indicator-arrow": { display: "none" },
    ".fc-now-indicator-line": {
      borderWidth: "2px 0 0",
      borderColor: "#FF0000 !important",
      zIndex: 100,
    },
    ".fc-timegrid-slot-label-cushion": {
      fontSize: "0.75rem",
      fontWeight: 600,
      color: "#6B7280",
      padding: "0 12px !important",

      verticalAlign: "top !important",
      marginTop: "-20px",
    },
    ".fc-timegrid-axis-cushion": { display: "none" },
    ".fc-timegrid-slot": { height: "70px !important" },
    ".fc-theme-standard td, .fc-theme-standard th": {
      borderColor: "#E5E5E5 !important",
    },
    ".fc-event": {
      borderRadius: "4px",
      margin: "1px !important",
    },
  };

  if (viewMode === "weekly") {
    return (
      <Box
        sx={{
          width: "100%",
          border: "1px solid #E5E5E5",
          borderRadius: "8px",
          overflow: "hidden",
          backgroundColor: "#fff",
        }}
      >
        <GlobalStyles
          styles={{
            ...commonGlobalStyles,
            ".fc-col-header-cell": {
              backgroundColor: "#F9FAFB",
              py: 1,
            },
            ".fc-timegrid": {
              minWidth: isMobile ? "1200px" : "auto",
            },
            ".fc-col-header-cell-cushion": {
              fontSize: "0.75rem",
              fontWeight: 700,
              color: "#374151",
              textTransform: "uppercase",
              padding: "8px 4px",
              textDecoration: "none !important",
            },
            ".fc-day-today": {
              backgroundColor: "rgba(34, 111, 226, 0.05) !important",
            },
            ".fc-timegrid-axis": {
              width: "60px !important",
            },
          }}
        />
        <FullCalendar
          ref={(el) => (calendarRefs.current[0] = el)}
          plugins={[timeGridPlugin, interactionPlugin]}
          initialView="timeGridWeek"
          headerToolbar={false}
          allDaySlot={false}
          slotMinTime={`${startHour.toString().padStart(2, "0")}:00:00`}
          slotMaxTime={`${endHour.toString().padStart(2, "0")}:00:00`}
          slotDuration="01:00:00"
          slotLabelFormat={{
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
          }}
          dayHeaderFormat={{
            weekday: "short",
            day: "numeric",
          }}
          height="auto"
          events={allEvents}
          eventContent={renderEventContent}
          editable={true}
          droppable={true}
          eventDrop={onEventDrop}
          nowIndicator={true}
          locale="pt-br"
          firstDay={0}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        border: "1px solid #E5E5E5",
        borderRadius: "8px",
        overflow: "hidden",
        backgroundColor: "#fff",
      }}
    >
      <GlobalStyles styles={commonGlobalStyles} />
      <Box
        sx={{
          display: "flex",
          width: "100%",
          overflowX: "auto",
        }}
      >
        {selectedCourts.map((court, index) => {
          const courtEvents = allEvents.filter(
            (e) => e.extendedProps.courtName === court,
          );
          const isFirstColumn = index === 0;

          return (
            <Box
              key={court}
              sx={{
                flex: 1,
                minWidth: isMobile ? "250px" : "0",
                borderLeft: isFirstColumn ? "none" : "1px solid #E5E5E5",
                "& .fc-header-toolbar": { display: "none" },
                "& .fc-timegrid-axis": {
                  display: isFirstColumn ? "table-cell" : "none",
                  width: "60px !important",
                },
                "& .fc-timegrid-slot-label": {
                  display: isFirstColumn ? "table-cell" : "none",
                  borderRight: "none",
                },
                "& .fc-col-header-cell": { display: "none" },
              }}
            >
              <Box
                sx={{
                  textAlign: "center",
                  py: 1.5,
                  backgroundColor: "#F9FAFB",
                  borderBottom: "1px solid #E5E5E5",
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {court}
                </Typography>
              </Box>

              <FullCalendar
                ref={(el) => (calendarRefs.current[index] = el)}
                plugins={[timeGridPlugin, interactionPlugin]}
                initialView="timeGridDay"
                headerToolbar={false}
                dayHeaders={false}
                allDaySlot={false}
                slotMinTime={`${startHour.toString().padStart(2, "0")}:00:00`}
                slotMaxTime={`${endHour.toString().padStart(2, "0")}:00:00`}
                slotDuration="01:00:00"
                slotLabelFormat={{
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: false,
                }}
                height="auto"
                events={courtEvents}
                eventContent={renderEventContent}
                editable={true}
                droppable={true}
                eventDrop={onEventDrop}
                nowIndicator={true}
                locale="pt-br"
              />
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default ModernCalendarView;
